import { dimOf, qtyInBase, basisOfLine, isPalletLine, isTruckLine, fmtInUnit, UNIT_TO_BASE } from "./bom";

// Availability check for a placed-order BOM snapshot (count + film lines).
export function buildAvailability(bom, compIndex, reservations) {
  const num = (v) => Number(v) || 0;
  const lookupStock = (componentId, typeId, designId) => {
    const c = compIndex[componentId];
    if (!c) return 0;
    if (typeId) {
      const t = (c.types || []).find((x) => x.id === typeId);
      if (t) {
        if (designId && t.design_stock) return num(t.design_stock[designId]);
        return num(t.stock_qty);
      }
    }
    return num(c.stock_qty);
  };
  const invUnitOf = (t) => {
    const rct = t?.roll_content_type;
    if ((rct === "ft" || rct === "m") && Number(t?.content_per_stock_unit) > 0) return rct;
    return t?.roll_weight_unit || "";
  };
  const isFilmLine = (b) => (compIndex[b.component_id]?.inventory_mode_effective === "film");
  return bom.filter((b) => b.is_count || isFilmLine(b)).map((b) => {
    const available = lookupStock(b.component_id, b.type_id, b.design_id);
    const t = (compIndex[b.component_id]?.types || []).find((x) => x.id === b.type_id);
    const f = b.unit === "count" || !b.unit ? 1 : (UNIT_TO_BASE[invUnitOf(t)] || 1);
    const required = b.unit === "count" || !b.unit ? b.required : b.required / f;
    const resEntry = reservations[b.design_id ? `${b.type_id}::${b.design_id}` : b.type_id];
    const reserved = Math.round(((resEntry?.reserved || 0) / f) * 100) / 100;
    const free = Math.round((available - reserved) * 100) / 100;
    const shortage = Math.max(0, required - free);
    let status = "Available";
    if (free < required) status = "Shortage";
    else if (free < required * 1.15) status = "Low";
    const unit = isFilmLine(b) ? invUnitOf(t) : "";
    return { name: b.name, type: b.type, required: Math.round(required * 100) / 100, available, reserved, free, shortage, status, unit, reservedOrders: resEntry?.orders || [] };
  });
}

// Shared BOM-snapshot builder used by the MO creator and the Product BOMs "Add Stock" dialog.
export function buildOrderBOM(p, components, { cfg, casePackSize, totalCases, totalPallets, totalTrucks, designId }) {
  const compIndex = Object.fromEntries((components || []).map((c) => [c.id, c]));
  const labelLine = (p.lines || []).find((l) => /label/i.test(l.component_name || ""));
  const selDesign = labelLine && designId ? (labelLine.designs || []).find((d) => d.id === designId) : null;
  const waterL = (p.lines || []).find((x) => dimOf(compIndex[x.component_id]) === "volume");
  let waterTotalL = 0;
  if (waterL) {
    const wc = compIndex[waterL.component_id];
    const wqb = qtyInBase(waterL, wc, cfg?.id);
    const wPerCase = basisOfLine(waterL, wc) === "per_bottle" ? wqb * casePackSize : wqb;
    waterTotalL = (wPerCase * totalCases) / 1000;
  }

  return (p.lines || []).filter((l) => !l.missing).flatMap((l) => {
    const comp = compIndex[l.component_id];
    const dim = dimOf(comp);
    const perBottleLevel = basisOfLine(l, comp) === "per_bottle";
    const palletLevel = isPalletLine(l, comp);
    const truckLevel = isTruckLine(l, comp);
    const isLabel = (comp && typeof comp.has_designs === "boolean") ? comp.has_designs : /label/i.test(l.component_name || "");
    const qb = qtyInBase(l, comp, cfg?.id);
    const perCase = perBottleLevel ? qb * casePackSize : qb;
    const total = truckLevel ? qb * totalTrucks : palletLevel ? qb * totalPallets : perCase * totalCases;

    const assocs = !isLabel && selDesign ? (selDesign.associations || []).filter((a) => a.component_id === l.component_id && a.variant_id) : [];
    if (selDesign && !isLabel && assocs.length === 0 && /additive/i.test(l.component_name || "")) return [];
    if (assocs.length > 0) {
      return assocs.map((a) => {
        const dose = Number(a.dose_g_per_l) > 0 ? Number(a.dose_g_per_l) : null;
        const req = dose ? dose * waterTotalL : total;
        return {
          component_id: l.component_id, name: l.component_name, type: a.variant_name || "",
          type_id: a.variant_id, design_id: "",
          required: dim === "count" ? Math.round(req) : Math.round(req * 100) / 100, unit: dim,
          is_count: dim === "count",
          display: dose ? `${Math.round(req * 100) / 100} g` : fmtInUnit(dim, req, l.qty_unit),
        };
      });
    }

    // resolved type + inventory keys
    let type = "";
    let invTypeId = l.type_id || "";
    let invDesignId = "";
    if (basisOfLine(l, comp) === "per_case") {
      const tid = (l.config_type_ids || {})[cfg?.id] || l.type_id;
      invTypeId = tid || invTypeId;
      const v = tid ? (comp?.types || []).find((t) => t.id === tid) : null;
      type = v ? v.name : "";
    }
    else type = l.type_name || "";
    if (!invTypeId && (comp?.types || []).length === 1) { invTypeId = comp.types[0].id; type = type || comp.types[0].name || ""; }
    if (isLabel) { invDesignId = designId; if (selDesign?.name) type = type ? `${type} · ${selDesign.name}` : selDesign.name; }

    return [{
      component_id: l.component_id, name: l.component_name, type,
      type_id: invTypeId, design_id: invDesignId,
      required: dim === "count" ? Math.round(total) : Math.round(total * 100) / 100, unit: dim,
      is_count: dim === "count",
      display: fmtInUnit(dim, total, l.qty_unit),
    }];
  });
}
